<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :info-cards="pageInfoCards"
  >
    <template #actions>
      <md-button class="md-info md-round" @click="refresh" :disabled="isLoading">
        <md-icon>refresh</md-icon>
        Refresh
      </md-button>
      <md-button class="md-warning md-round" @click="resetFilters" title="Reset Filters">
        <md-icon>restart_alt</md-icon>
        Reset Filters
      </md-button>
    </template>

    <template #content>
      <!-- Filters Bar -->
      <div class="filters-bar">
        <!-- Row 1: Location -->
        <div class="filter-row md-layout md-gutter">
          <div class="md-layout-item md-size-25 md-small-size-50">
            <select-input
                v-model="filters.station"
                track-by="id"
                name="name"
                placeholder="All Stations"
                :options="stations"
                :allow-empty="true"
                :reduce-to-value="false"
                @option:selected="onStationChange"
            />
          </div>

          <div class="md-layout-item md-size-25 md-small-size-50">
            <select-input
                v-model="filters.department"
                track-by="id"
                name="name"
                placeholder="All Departments"
                :options="departments"
                :allow-empty="true"
                :reduce-to-value="false"
                :disabled="!filters.station"
                @option:selected="onFilterChange"
            />
          </div>

          <div class="md-layout-item md-size-25 md-small-size-50">
            <select-input
                v-model="filters.vendor"
                track-by="id"
                name="name"
                placeholder="All Vendors"
                :options="vendors"
                :allow-empty="true"
                :reduce-to-value="false"
                :disabled="!filters.station"
                @option:selected="onFilterChange"
            />
          </div>

          <div class="md-layout-item md-size-25 md-small-size-50">
            <select-input
                v-model="filters.workplace"
                track-by="id"
                name="name"
                placeholder="All Workplaces"
                :options="workplaces"
                :allow-empty="true"
                :reduce-to-value="false"
                @option:selected="onFilterChange"
            />
          </div>
        </div>

        <!-- Row 2: Detail Filters -->
        <div class="filter-row md-layout md-gutter">
          <div class="md-layout-item md-size-20 md-small-size-50">
            <select-input
                v-model="filters.activity"
                track-by="id"
                name="name"
                placeholder="All Activities"
                :options="activities"
                :allow-empty="true"
                :reduce-to-value="false"
                @option:selected="onFilterChange"
            />
          </div>

          <div class="md-layout-item md-size-16 md-small-size-50">
            <md-field>
              <label>Shift</label>
              <md-select v-model="filters.shift" @md-selected="onFilterChange">
                <md-option value="">All Shifts</md-option>
                <md-option v-for="s in shifts" :key="s.id" :value="s.id">
                  {{ s.name }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-16 md-small-size-50">
            <md-field>
              <label>From Date</label>
              <md-input type="date" v-model="filters.shift_date" @change="onFilterChange" />
            </md-field>
          </div>

          <div class="md-layout-item md-size-16 md-small-size-50">
            <md-field>
              <label>To Date</label>
              <md-input type="date" v-model="filters.end_date" @change="onFilterChange" />
            </md-field>
          </div>

          <div class="md-layout-item md-size-16 md-small-size-50">
            <md-field>
              <label>Guidance #</label>
              <md-input
                  type="text"
                  v-model="filters.guidanceNumber"
                  placeholder="Search guidance..."
                  @input="onSearchGuidanceDebounced"
              />
            </md-field>
          </div>

          <div class="md-layout-item md-size-16 md-small-size-50">
            <md-field>
              <label>Supervisor Status</label>
              <md-select v-model="filters.supervisor_checkout_at" @md-selected="onFilterChange">
                <md-option value="">All</md-option>
                <md-option value="supervisor_checkin_at">Supervisor Checked In</md-option>
                <md-option value="supervisor_checkout_at">Supervisor Checked Out</md-option>
                <md-option value="no_supervisor_checkin_at">Not Checked In</md-option>
                <md-option value="no_supervisor_checkout_at">Not Checked Out</md-option>
              </md-select>
            </md-field>
          </div>
        </div>

        <!-- Row 3: Assignment Toggles -->
        <div class="filter-row-assignment">
          <md-checkbox
              v-model="filters.show_unassigned"
              class="assignment-checkbox unassigned"
              @change="onFilterChange"
          >
            Show Unassigned
          </md-checkbox>

          <md-checkbox
              v-model="filters.show_assigned"
              class="assignment-checkbox assigned"
              @change="onFilterChange"
          >
            Show Assigned
          </md-checkbox>
        </div>
      </div>

      <!-- Data Table -->
      <table-data
          ref="table"
          title="Labour Schedules (Take On)"
          :columns="columns"
          :data="schedules"
          :total="total"
          :loading="isLoading"
          loading-text="Loading labour schedules..."
          :searchable="true"
          search-placeholder="Search by name, takeon, or guidance..."
          storage-key="labour-schedules-list"
          empty-message="No labour schedules found"
          @fetch="onTableFetch"
      >
        <template #toolbar-right>
          <md-button class="md-info md-round" @click="refresh">
            <md-icon>refresh</md-icon>
            Refresh
          </md-button>
        </template>

        <!-- Labourer -->
        <template #cell-labourer="{ item }">
          <div class="labourer-cell">
            <strong v-if="item.labourer">{{ getLabourerName(item.labourer) }}</strong>
            <span v-else class="unassigned-text">Not Assigned</span>
            <chip v-if="item.takeon_number" type="danger" size="small" class="takeon-chip">
              {{ item.takeon_number }}
            </chip>
          </div>
        </template>

        <!-- Workplace -->
        <template #cell-workplace="{ item }">
          <div class="workplace-cell">
            <span>{{ item.workplace ? item.workplace.name : 'N/A' }}</span>
            <chip
                v-if="item.guidance"
                type="info"
                size="small"
                class="guidance-chip"
            >
              {{ item.guidance.guidance_number }}
            </chip>
          </div>
        </template>

        <!-- Activity -->
        <template #cell-activity="{ item }">
          {{ item.activity ? item.activity.name : 'N/A' }}
        </template>

        <!-- Shift -->
        <template #cell-shift="{ item }">
          {{ item.shift ? item.shift.name : 'N/A' }}
        </template>

        <!-- Shift Date -->
        <template #cell-shift_date="{ item }">
          {{ item.schedule ? item.schedule.shift_date : 'N/A' }}
        </template>

        <!-- Shift Time -->
        <template #cell-shift_time="{ item }">
          <span v-if="item.schedule">
            {{ item.schedule.start_hour }} - {{ item.schedule.end_hour }}
          </span>
          <span v-else>—</span>
        </template>

        <!-- Status -->
        <template #cell-status="{ item }">
          <chip :type="getStatusBadge(item.status)">
            {{ getStatusLabel(item.status) }}
          </chip>
        </template>

        <!-- Method -->
        <template #cell-method="{ item }">
          <chip :type="getMethodBadge(item.check_in_type)" size="small">
            {{ getMethodLabel(item.check_in_type) }}
          </chip>
        </template>

        <!-- Check In -->
        <template #cell-checkin="{ item }">
          <div v-if="item.checkin_at" class="time-display">
            <md-icon class="time-icon">login</md-icon>
            {{ formatDateTime(item.checkin_at) }}
          </div>
          <md-button
              v-else
              class="md-icon-button md-success"
              @click="goCheckIn(item, item)"
              :disabled="rowStatus[item.id] === 'loading' || !getLabourerId(item)"
              title="Check In"
          >
            <md-icon v-if="rowStatus[item.id] === 'loading'">hourglass_empty</md-icon>
            <md-icon v-else>login</md-icon>
          </md-button>
        </template>

        <!-- Checkin Supervisor -->
        <template #cell-supervisor_checkin="{ item }">
          <div v-if="item.checkin_supervisor_reaction" class="time-display">
            <chip :type="getReactionBadge(item.checkin_supervisor_reaction)" size="small">
              {{ getReactionLabel(item.checkin_supervisor_reaction) }}
            </chip>
          </div>
          <md-button
              v-else
              class="md-icon-button md-info"
              @click="supervisorCheckIn(item, item)"
              :disabled="rowStatus[item.id] === 'loading' || !item.checkin_at || !getLabourerId(item)"
              title="Checkin Supervisor"
          >
            <md-icon v-if="rowStatus[item.id] === 'loading'">hourglass_empty</md-icon>
            <md-icon v-else>verified_user</md-icon>
          </md-button>
        </template>

        <!-- Checkout Supervisor -->
        <template #cell-supervisor_checkout="{ item }">
          <div v-if="item.checkout_supervisor_reaction" class="time-display">
            <chip :type="getReactionBadge(item.checkout_supervisor_reaction)" size="small">
              {{ getReactionLabel(item.checkout_supervisor_reaction) }}
            </chip>
          </div>
          <md-button
              v-else
              class="md-icon-button md-warning"
              @click="supervisorCheckout(item, item)"
              :disabled="rowStatus[item.id] === 'loading' || !item.checkin_supervisor_reaction || !getLabourerId(item)"
              title="Checkout Supervisor"
          >
            <md-icon v-if="rowStatus[item.id] === 'loading'">hourglass_empty</md-icon>
            <md-icon v-else>exit_to_app</md-icon>
          </md-button>
        </template>

        <!-- Check Out -->
        <template #cell-checkout="{ item }">
          <div v-if="item.checkout_at" class="time-display">
            <md-icon class="time-icon">logout</md-icon>
            {{ formatDateTime(item.checkout_at) }}
          </div>
          <md-button
              v-else
              class="md-icon-button md-primary"
              @click="goCheckOut(item, item)"
              :disabled="rowStatus[item.id] === 'loading' || !item.supervisor_checkout_at || !getLabourerId(item)"
              title="Check Out"
          >
            <md-icon v-if="rowStatus[item.id] === 'loading'">hourglass_empty</md-icon>
            <md-icon v-else>logout</md-icon>
          </md-button>
        </template>

        <!-- Actions -->
        <template #actions="{ item }">
          <div class="action-buttons">
            <md-button
                class="md-icon-button md-primary"
                @click="goToView(item.id)"
                :disabled="rowStatus[item.id] === 'loading'"
                title="View Details"
            >
              <md-icon>visibility</md-icon>
            </md-button>

            <md-button
                v-if="getLabourerId(item)"
                class="md-icon-button md-success"
                @click="syncPunch(item, item)"
                :disabled="rowStatus[item.id] === 'loading'"
                title="Sync Punch"
            >
              <md-icon v-if="rowStatus[item.id] === 'loading'">hourglass_empty</md-icon>
              <md-icon v-else>sync</md-icon>
            </md-button>

            <md-button
                v-if="item.checkout_at && can('cancel-labourer-schedule-checkIn')"
                class="md-icon-button md-danger"
                @click="resetCheckIn(item)"
                :disabled="rowStatus[item.id] === 'loading'"
                title="Reset"
            >
              <md-icon>restore</md-icon>
            </md-button>
          </div>
        </template>

        <template #empty-actions>
          <p class="text-muted">Try adjusting your filters or search criteria.</p>
        </template>
      </table-data>

      <!-- Supervisor Action Modals -->
      <supervisor-action-modal
          :visible.sync="dialogVisible"
          title="Supervisor Check-in"
          :reactions="['on_time', 'late', 'absent']"
          :form="dialogForm"
          @submit="submitSupervisorCheckIn"
          @close="dialogVisible = false"
          @invalid="showValidationError"
      />

      <supervisor-action-modal
          :visible.sync="checkoutDialogVisible"
          title="Supervisor Checkout"
          :reactions="['completed', 'partial', 'early_departure', 'late_departure']"
          :form="checkoutForm"
          @submit="submitSupervisorCheckout"
          @close="checkoutDialogVisible = false"
          @invalid="showValidationError"
      />
    </template>


  </page-layout>
</template>

<script>
import _ from 'lodash';
import PageLayout from '@/components/Layouts/PageLayout.vue';
import TableData from '@/components/DataTable/TableData.vue';
import Chip from '@/components/Chip.vue';
import SelectInput from '@/components/Inputs/SelectInput.vue';
import SupervisorActionModal from '@/pages/vendors/labourerSchedules/components/SupervisorActionModal.vue';
import PageMixin from '@/mixins/page-mixin';
import formMixin from '@/mixins/form-mixin';
import DateFormatMixin from '@/mixins/date-format-mixin';
import Swal from 'sweetalert2';
import Vuex from 'vuex';

export default {
  name: 'LabourSchedulesListPage',

  components: {
    PageLayout: PageLayout,
    TableData: TableData,
    Chip: Chip,
    SelectInput: SelectInput,
    SupervisorActionModal: SupervisorActionModal
  },

  mixins: [PageMixin, formMixin, DateFormatMixin],

  data() {
    return {
      filters: {
        station: null,
        department: null,
        vendor: null,
        guidanceNumber: '',
        shift: '',
        activity: null,
        workplace: null,
        shift_date: '',
        end_date: '',
        supervisor_checkout_at: '',
        show_unassigned: false,
        show_assigned: true
      },

      stations: [],
      departments: [],
      vendors: [],
      workplaces: [],
      activities: [],
      shifts: [],
      schedules: [],
      total: 0,
      isLoading: false,

      tableState: {
        page: 1,
        perPage: 10,
        search: ''
      },

      columns: [
        { field: 'labourer', label: 'Labourer' },
        { field: 'workplace', label: 'Workplace' },
        { field: 'activity', label: 'Activity' },
        { field: 'shift', label: 'Shift' },
        { field: 'shift_date', label: 'Shift Date' },
        { field: 'shift_time', label: 'Shift Time' },
        { field: 'status', label: 'Status' },
        { field: 'method', label: 'Method' },
        { field: 'checkin', label: 'Check In' },
        { field: 'supervisor_checkin', label: 'SP IN' },
        { field: 'supervisor_checkout', label: 'SP OUT' },
        { field: 'checkout', label: 'Check Out' }
      ],

      dialogVisible: false,
      dialogForm: { reaction: '', comment: '' },
      activeRow: null,
      rowStatus: {},

      checkoutDialogVisible: false,
      checkoutForm: { reaction: '', comment: '' },
      activeCheckoutRow: null,

      userDepartment: null,
      hasRestoredState: false,
      stateKey: 'labour_schedules_list_state'
    };
  },

  computed: {
    ...Vuex.mapState({
      me: state => state.profile.me
    }),

    pageTitle() {
      return 'Labour Schedules';
    },

    pageBreadcrumb() {
      return [
        { label: 'Dashboard', path: '/' },
        { label: 'Labour Schedules' }
      ];
    },

    pageInfoCards() {
      return [
        {
          icon: 'assignment',
          label: 'Total Schedules',
          value: this.total || 0,
          color: 'primary'
        },
        {
          icon: 'people',
          label: 'On This Page',
          value: this.schedules.length,
          color: 'info'
        },
        {
          icon: 'pending_actions',
          label: 'Pending Check-in',
          value: this.countPendingCheckIn(),
          color: 'warning'
        },
        {
          icon: 'task_alt',
          label: 'Completed',
          value: this.countCompleted(),
          color: 'success'
        }
      ];
    }
  },

  created() {
    this.restoreState();
    this.loadData();
  },

  beforeDestroy() {
    // Nothing to clean up here, DataTable handles its own state
  },

  beforeRouteLeave(to, from, next) {
    this.saveFiltersState();
    next();
  },

  methods: {
    // ============================================================
    // STATE
    // ============================================================
    saveFiltersState() {
      var state = {
        filters: {
          station: this.filters.station,
          department: this.filters.department,
          vendor: this.filters.vendor,
          guidanceNumber: this.filters.guidanceNumber,
          shift: this.filters.shift,
          activity: this.filters.activity,
          workplace: this.filters.workplace,
          shift_date: this.filters.shift_date,
          end_date: this.filters.end_date,
          supervisor_checkout_at: this.filters.supervisor_checkout_at,
          show_unassigned: this.filters.show_unassigned,
          show_assigned: this.filters.show_assigned
        },
        timestamp: Date.now()
      };
      sessionStorage.setItem(this.stateKey, JSON.stringify(state));
    },

    restoreState() {
      var saved = sessionStorage.getItem(this.stateKey);
      if (!saved) return false;
      try {
        var state = JSON.parse(saved);
        if (Date.now() - state.timestamp > 30 * 60 * 1000) {
          sessionStorage.removeItem(this.stateKey);
          return false;
        }
        if (state.filters) {
          Object.assign(this.filters, state.filters);
        }
        this.hasRestoredState = true;
        return true;
      } catch (e) {
        console.error('Error restoring filters state', e);
        return false;
      }
    },

    // ============================================================
    // LOOKUPS
    // ============================================================
    async loadData() {
      try {
        await Promise.all([
          this.loadStations(),
          this.loadShifts(),
          this.loadActivities(),
          this.loadWorkplaces()
        ]);

        if (!this.filters.shift_date) this.filters.shift_date = this.formatDateYMD(new Date());
        if (!this.filters.end_date) this.filters.end_date = this.formatDateYMD(new Date());

        if (this.me && this.me.model && this.me.model.department && this.me.model.department.station) {
          this.userDepartment = this.me.model.department;
          this.filters.station = this.userDepartment.station.id;
          this.filters.department = this.userDepartment.id;
        } else if (this.me && this.me.model && this.me.model.department) {
          this.userDepartment = this.me.model.department;
          this.filters.department = this.userDepartment.id;
        }

        if (this.filters.station) {
          await this.loadStationDependencies(this.filters.station);
        }
      } catch (e) {
        console.error('loadData error', e);
        this.$store.dispatch('alerts/error', 'Failed to load initial data');
      }
    },

    async loadStations() {
      try {
        await this.$store.dispatch('stations/list', { page: { number: 1, size: 100 } });
        this.stations = this.$store.getters['stations/list'] || [];
      } catch (e) {
        console.error('loadStations error', e);
      }
    },

    async loadDepartments(stationId) {
      if (!stationId) { this.departments = []; return; }
      try {
        await this.$store.dispatch('departments/list', {
          page: { number: 1, size: 1000 },
          filter: { station: stationId }
        });
        this.departments = this.$store.getters['departments/list'] || [];
      } catch (e) {
        console.error('loadDepartments error', e);
        this.departments = [];
      }
    },

    async loadVendors(stationId) {
      if (!stationId) { this.vendors = []; return; }
      try {
        await this.$store.dispatch('vendors/list', {
          page: { number: 1, size: 1000 },
          filter: { station: stationId }
        });
        this.vendors = this.$store.getters['vendors/list'] || [];
      } catch (e) {
        console.error('loadVendors error', e);
        this.vendors = [];
      }
    },

    async loadWorkplaces() {
      try {
        await this.$store.dispatch('workplaces/list', { page: { size: 1100 } });
        this.workplaces = this.$store.getters['workplaces/list'] || [];
      } catch (e) {
        console.error('loadWorkplaces error', e);
      }
    },

    async loadActivities() {
      try {
        await this.$store.dispatch('activities/list', { page: { size: 1000 } });
        this.activities = this.$store.getters['activities/list'] || [];
      } catch (e) {
        console.error('loadActivities error', e);
      }
    },

    async loadShifts() {
      try {
        await this.$store.dispatch('shifts/list', { page: { size: 1000 } });
        this.shifts = this.$store.getters['shifts/list'] || [];
      } catch (e) {
        console.error('loadShifts error', e);
      }
    },

    async loadStationDependencies(stationId) {
      await Promise.all([
        this.loadDepartments(stationId),
        this.loadVendors(stationId)
      ]);
    },

    async onStationChange() {
      this.filters.department = null;
      this.filters.vendor = null;
      this.departments = [];
      this.vendors = [];

      if (this.filters.station) {
        await this.loadStationDependencies(this.filters.station.id || this.filters.station);
      }
      this.onFilterChange();
    },

    // ============================================================
    // TABLE FETCH
    // ============================================================
    onTableFetch(state) {
      this.tableState = state;
      this.getList();
    },

    async getList() {
      this.isLoading = true;

      try {
        var params = this.buildListParams(this.tableState.page);

        await this.$store.dispatch('labourerSchedules/list', params);
        this.schedules = this.$store.getters['labourerSchedules/list'] || [];
        this.total = this.$store.getters['labourerSchedules/listTotal'] || 0;
        this.saveFiltersState();
      } catch (e) {
        console.error('getList error', e);
        this.$store.dispatch('alerts/error', 'Failed to load labour schedules');
        this.schedules = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },

    buildListParams(page) {
      var f = this.filters;
      return {
        include: 'labourer,labourerAttendance,workplace,activity,schedule,shift',
        filter: Object.assign(
            {},
            this.tableState.search ? { queryKey: this.tableState.search } : {},
            f.station ? { station: f.station.id || f.station } : {},
            f.department ? { department: f.department.id || f.department } : {},
            f.vendor ? { vendor: f.vendor.id || f.vendor } : {},
            f.shift ? { shift: f.shift } : {},
            f.shift_date ? { shift_date: f.shift_date } : {},
            f.end_date ? { end_date: f.end_date } : {},
            f.guidanceNumber ? { guidanceNumber: f.guidanceNumber } : {},
            f.workplace ? { workplace: f.workplace.id || f.workplace } : {},
            f.activity ? { activity: f.activity.id || f.activity } : {},
            f.supervisor_checkout_at !== '' ? { checkoutSupervisor: f.supervisor_checkout_at } : {},
            (f.show_unassigned === true && f.show_assigned === false) ? { is_assigned: 'unassigned' } : {},
            (f.show_assigned === true && f.show_unassigned === false) ? { is_assigned: 'assigned' } : {}
        ),
        page: {
          number: page,
          size: this.tableState.perPage
        }
      };
    },

    refresh() {
      if (this.$refs.table) {
        this.$refs.table.emitFetch();
      } else {
        this.getList();
      }
    },

    onFilterChange() {
      if (this.$refs.table) {
        this.$refs.table.currentPage = 1;
        this.$refs.table.persistState();
        this.$refs.table.emitFetch();
      } else {
        this.getList();
      }
    },

    onSearchGuidanceDebounced: _.debounce(function () {
      this.onFilterChange();
    }, 500),

    resetFilters() {
      this.filters = {
        station: null,
        department: null,
        vendor: null,
        guidanceNumber: '',
        shift: '',
        activity: null,
        workplace: null,
        shift_date: this.formatDateYMD(new Date()),
        end_date: this.formatDateYMD(new Date()),
        supervisor_checkout_at: '',
        show_unassigned: false,
        show_assigned: true
      };

      this.departments = [];
      this.vendors = [];

      if (this.$refs.table) {
        this.$refs.table.setSearch('');
        this.$refs.table.currentPage = 1;
        this.$refs.table.persistState();
      }

      this.onFilterChange();
      this.$store.dispatch('alerts/info', 'Filters reset');
    },

    // ============================================================
    // ACTIONS
    // ============================================================
    async goCheckIn(item) {
      if (!this.getLabourerId(item)) {
        await this.$store.dispatch('alerts/error', 'Cannot check in: No labourer assigned.');
        return;
      }
      if (!item.labourer) {
        await this.$store.dispatch('alerts/error', 'Please assign a labourer first.');
        return;
      }
      if (!item.labourer.nida_number) {
        await this.$store.dispatch('alerts/error', 'Please assign a NIDA number first.');
        return;
      }

      try {
        this.$set(this.rowStatus, item.id, 'loading');
        await this.$store.dispatch('labourerSchedules/checkin', item);
        await this.reloadRow(item);
        this.$store.dispatch('alerts/success', 'Check-in completed');
        this.$set(this.rowStatus, item.id, 'success');
      } catch (e) {
        console.error('checkIn error', e);
        this.$set(this.rowStatus, item.id, 'error');
        this.notifyException(e);
        setTimeout(() => this.$set(this.rowStatus, item.id, ''), 5000);
      } finally {
        this.$set(this.rowStatus, item.id, '');
      }
    },

    async goCheckOut(item) {
      if (!this.getLabourerId(item)) {
        await this.$store.dispatch('alerts/error', 'Cannot check out: No labourer assigned.');
        return;
      }
      if (!item.supervisor_checkout_at) {
        this.$store.dispatch('alerts/error', 'Supervisor check-out required first.');
        return;
      }

      try {
        this.$set(this.rowStatus, item.id, 'loading');
        await this.$store.dispatch('labourerSchedules/checkout', item);
        await this.reloadRow(item);
        this.$store.dispatch('alerts/success', 'Check-out completed');
        this.$set(this.rowStatus, item.id, 'success');
      } catch (e) {
        console.error('checkOut error', e);
        this.$set(this.rowStatus, item.id, 'error');
        this.notifyException(e);
        setTimeout(() => this.$set(this.rowStatus, item.id, ''), 5000);
      } finally {
        this.$set(this.rowStatus, item.id, '');
      }
    },

    supervisorCheckIn(item) {
      if (!item.checkin_at) {
        this.$store.dispatch('alerts/error', 'Check-in required first.');
        return;
      }
      if (!this.getLabourerId(item)) {
        this.$store.dispatch('alerts/error', 'No labourer assigned.');
        return;
      }

      this.dialogForm.reaction = '';
      this.dialogForm.comment = '';
      this.activeRow = item;
      this.dialogVisible = true;
    },

    async submitSupervisorCheckIn(formData) {
      if (!formData.reaction) {
        this.$store.dispatch('alerts/error', 'Reaction is required.');
        return;
      }

      var item = this.activeRow;
      this.$set(this.rowStatus, item.id, 'loading');

      try {
        await this.$store.dispatch('labourerSchedules/checkinSupervisor', {
          id: item.id,
          checkin_supervisor_reaction: formData.reaction,
          checkin_supervisor_comment: formData.comment,
          checkin_supervisor_id: this.me ? this.me.id : null
        });
        await this.reloadRow(item);
        this.$set(this.rowStatus, item.id, 'success');
        this.dialogVisible = false;
        this.$store.dispatch('alerts/success', 'Supervisor check-in completed');
      } catch (e) {
        console.error('supervisorCheckIn error', e);
        this.$set(this.rowStatus, item.id, 'error');
        this.notifyException(e);
        setTimeout(() => this.$set(this.rowStatus, item.id, ''), 5000);
      } finally {
        this.$set(this.rowStatus, item.id, '');
        this.activeRow = null;
      }
    },

    supervisorCheckout(item) {
      console.log('item checkount');
      if (!item.checkin_supervisor_reaction) {
        this.notifyError('Supervisor check-in required first.');
        return;
      }
      if (!this.getLabourerId(item)) {
        this.notifyError('No labourer assigned.');
        return;
      }

      this.checkoutForm.reaction = '';
      this.checkoutForm.comment = '';
      this.activeCheckoutRow = item;
      this.checkoutDialogVisible = true;
      console.log('checkoutDialogVisible', item);
    },

    async submitSupervisorCheckout(formData) {
      if (!formData.reaction) {
        this.$store.dispatch('alerts/error', 'Reaction is required.');
        return;
      }

      var item = this.activeCheckoutRow;
      this.$set(this.rowStatus, item.id, 'loading');

      try {
        await this.$store.dispatch('labourerSchedules/checkoutSupervisor', {
          id: item.id,
          checkout_supervisor_reaction: formData.reaction,
          checkout_supervisor_comment: formData.comment,
          checkout_supervisor_id: this.me ? this.me.id : null
        });
        await this.reloadRow(item);
        this.$set(this.rowStatus, item.id, 'success');
        this.checkoutDialogVisible = false;
        this.$store.dispatch('alerts/success', 'Supervisor check-out completed');
      } catch (e) {
        console.error('supervisorCheckout error', e);
        this.$set(this.rowStatus, item.id, 'error');
        this.notifyException(e);
        setTimeout(() => this.$set(this.rowStatus, item.id, ''), 5000);
      } finally {
        this.$set(this.rowStatus, item.id, '');
        this.activeCheckoutRow = null;
      }
    },

    async syncPunch(item) {
      try {
        this.$set(this.rowStatus, item.id, 'loading');
        var labourerName = item.labourer ? this.getLabourerName(item.labourer) : '';
        this.$store.dispatch('alerts/info', 'Syncing punch data for ' + labourerName + '...');

        await this.$store.dispatch('labourerSchedules/syncPunchs', item);

        var updatedSchedule = this.$store.getters['labourerSchedules/labourerSchedule'];
        if (updatedSchedule) {
          var idx = this.schedules.findIndex(s => s.id === item.id);
          if (idx >= 0) this.$set(this.schedules, idx, Object.assign({}, item, {
            checkin_at: updatedSchedule.checkin_at,
            checkout_at: updatedSchedule.checkout_at,
            status: updatedSchedule.status
          }));

          if (updatedSchedule.message) {
            Swal.fire(`${labourerName} Punchs`, `${updatedSchedule.message}`, 'info');
          }
        }

        this.$set(this.rowStatus, item.id, 'success');
      } catch (e) {
        console.error('syncPunch error', e);
        this.$set(this.rowStatus, item.id, 'error');
        this.notifyException(e);
        setTimeout(() => this.$set(this.rowStatus, item.id, ''), 5000);
      } finally {
        this.$set(this.rowStatus, item.id, '');
      }
    },

    async resetCheckIn(item) {
      var result = await Swal.fire({
        title: 'Reset CheckIn/CheckOut Record?',
        html: '<p>Reset TakeOn <strong>' + item.takeon_number + '</strong> for ' +
            (item.labourer ? (item.labourer.first_name + ' ' + item.labourer.last_name) : '') + '?</p>',
        icon: 'warning',
        input: 'textarea',
        inputPlaceholder: 'Enter reason for reset (min 5 characters)',
        showCancelButton: true,
        confirmButtonText: 'Reset',
        cancelButtonText: 'Cancel',
        preConfirm: function (value) {
          if (!value || value.length < 5) {
            Swal.showValidationMessage('Please provide a reason (at least 5 characters)');
          }
          return value;
        }
      });

      if (!result.value) return;

      try {
        this.$set(this.rowStatus, item.id, 'loading');
        await this.$store.dispatch('labourerSchedules/reset', {
          id: item.id,
          comment: result.value
        });
        this.$store.dispatch('alerts/success', 'Schedule reset successfully');
        this.$set(this.rowStatus, item.id, 'success');
        this.refresh();
      } catch (e) {
        console.error('resetCheckIn error', e);
        this.$set(this.rowStatus, item.id, 'error');
        this.notifyException(e);
        setTimeout(() => this.$set(this.rowStatus, item.id, ''), 5000);
      } finally {
        this.$set(this.rowStatus, item.id, '');
      }
    },

    async reloadRow(item) {
      await this.$store.dispatch('labourerSchedules/view', {
        id: item.id,
        include: 'labourer,labourerAttendance,workplace,activity,schedule,shift,department,guidance'
      });
      var fresh = this.$store.getters['labourerSchedules/labourerSchedule'];
      var idx = this.schedules.findIndex(s => s.id === item.id);
      if (idx >= 0 && fresh) this.$set(this.schedules, idx, fresh);
    },

    showValidationError(msg) {
      this.$store.dispatch('alerts/error', msg);
    },

    notifyException(error) {
      console.error('Error:', error);
      if (error && error.response && error.response.data && error.response.data.errors) {
        this.$store.dispatch('alerts/error', error.response.data.errors[0].detail || 'An error occurred');
      } else {
        this.$store.dispatch('alerts/error', 'An error occurred');
      }
    },

    // ============================================================
    // HELPERS
    // ============================================================
    getLabourerName(labourer) {
      if (!labourer) return 'N/A';
      var parts = [];
      if (labourer.first_name) parts.push(labourer.first_name);
      if (labourer.middle_name) parts.push(labourer.middle_name);
      if (labourer.last_name) parts.push(labourer.last_name);
      return parts.length ? parts.join(' ') : 'N/A';
    },

    getLabourerId(labourerSchedule) {
      var id = labourerSchedule.labourer ? labourerSchedule.labourer.id : null;
      return id && id !== '' ? id : null;
    },

    getStatusLabel(status) {
      var labels = {
        'pending': 'Pending',
        'checked_in': 'Checked In',
        'confirmed': 'Confirmed',
        'checked_out': 'Checked Out',
        'completed': 'Completed',
        'Check In': 'Check In',
        'Checkin Supervisor': 'Checkin Supervisor',
        'Checkout Supervisor': 'Checkout Supervisor',
        'Check Out': 'Check Out'
      };
      return labels[status] || status || 'Unknown';
    },

    getStatusBadge(status) {
      var badges = {
        'pending': 'warning',
        'checked_in': 'info',
        'confirmed': 'success',
        'checked_out': 'warning',
        'completed': 'success',
        'Check In': 'info',
        'Checkin Supervisor': 'success',
        'Checkout Supervisor': 'warning',
        'Check Out': 'success'
      };
      return badges[status] || 'info';
    },

    getMethodLabel(type) {
      var labels = { 'terminal': 'Terminal', 'manual': 'Manual', 'auto': 'Auto' };
      return labels[type] || type || 'N/A';
    },

    getMethodBadge(type) {
      var badges = { 'terminal': 'info', 'manual': 'warning', 'auto': 'success' };
      return badges[type] || 'info';
    },

    getReactionLabel(reaction) {
      var labels = {
        'on_time': 'On Time',
        'late': 'Late',
        'absent': 'Absent',
        'completed': 'Completed',
        'partial': 'Partial',
        'early_departure': 'Early Departure',
        'late_departure': 'Late Departure',
        'present': 'Present'
      };
      return labels[reaction] || reaction || 'N/A';
    },

    getReactionBadge(reaction) {
      var badges = {
        'on_time': 'success',
        'late': 'warning',
        'absent': 'danger',
        'completed': 'success',
        'partial': 'warning',
        'early_departure': 'warning',
        'late_departure': 'warning',
        'present': 'success'
      };
      return badges[reaction] || 'info';
    },

    formatDateTime(value) {
      if (!value) return '';
      try {
        var date = new Date(String(value).replace(' ', 'T'));
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return value;
      }
    },

    formatDateYMD(date) {
      if (!date) return null;
      var d = new Date(date);
      var year = d.getFullYear();
      var month = String(d.getMonth() + 1).padStart(2, '0');
      var day = String(d.getDate()).padStart(2, '0');
      return year + '-' + month + '-' + day;
    },

    countPendingCheckIn() {
      return this.schedules.filter(function (s) { return !s.checkin_at; }).length;
    },

    countCompleted() {
      return this.schedules.filter(function (s) { return !!s.checkout_at; }).length;
    },

    goToView(id) {
      this.saveFiltersState();
      this.$router.push({ name: 'View Labourer Schedule', params: { id: id } });
    }
  }
};
</script>

<style lang="scss" scoped>
/* ============================================================
   FILTERS BAR
   ============================================================ */
.filters-bar {
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 20px;
}

.filter-row {
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
}

.filter-row-assignment {
  display: flex;
  gap: 24px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e8e8e8;
  flex-wrap: wrap;
}

//.assignment-checkbox {
//  &.unassigned /deep/ .md-checkbox-label {
//    font-weight: 600;
//    color: #ff9800;
//  }
//
//  &.assigned /deep/ .md-checkbox-label {
//    font-weight: 600;
//    color: #4caf50;
//  }
//}

/* ============================================================
   TABLE CELLS
   ============================================================ */
.labourer-cell,
.workplace-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 130px;

  strong {
    font-size: 13px;
    color: #333;
  }

  .takeon-chip,
  .guidance-chip {
    align-self: flex-start;
    margin-top: 2px;
  }
}

.unassigned-text {
  color: #ff9800;
  font-weight: 600;
  font-style: italic;
  font-size: 13px;
}

.time-display {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #555;

  .time-icon {
    font-size: 14px !important;
    width: 14px !important;
    height: 14px !important;
    color: #667eea;
  }
}

.action-buttons {
  display: flex;
  gap: 2px;
  flex-wrap: wrap;
  justify-content: center;
}

.text-muted {
  color: #999;
  font-size: 13px;
}

/* ============================================================
   RESPONSIVE
   ============================================================ */
@media (max-width: 768px) {
  .filter-row .md-layout-item {
    margin-bottom: 10px;
  }

  .filter-row-assignment {
    flex-direction: column;
    gap: 8px;
  }

  .action-buttons {
    flex-direction: column;
  }
}
</style>
